import { cloneDeep } from 'es-toolkit';

import ExternalTour from '../../shared/model/ExternalTour';
import VrModel from '../../shared/model/VrModel';

import type { CurrentPropertyProps } from '../../shared/model/CurrentProperty';
import type { ExternalTourProps } from '../../shared/model/ExternalTour';
import type { FloorPlanProps } from '../../shared/model/FloorPlan';
import type { UnitProps } from '../../shared/model/Unit';
import type { VrModelProps } from '../../shared/model/VrModel';

type TargetFloorPlanProps = FloorPlanProps & { units: UnitProps };

const FLOOR_PLAN = 'floorPlan';
const UNIT = 'unit';
const AMENITY = 'amenity';
const SINGLE_FAMILY = 'singleFamily';
const TOUR_TYPE = {
  [FLOOR_PLAN]: 'FloorPlan',
  [UNIT]: 'Unit',
  [AMENITY]: 'Amenity',
  [SINGLE_FAMILY]: 'SingleFamily',
} as const;
export type KeysOfTourType = keyof typeof TOUR_TYPE;

const getDefaultTourLabel = (
  tourType: KeysOfTourType,
  floorPlanIndex: null | number = null,
  unitIndex: null | number = null,
  {
    vrModels = [],
    externalTours = [],
    floorplans = [],
    units = [],
  }: Partial<{
    vrModels: Array<unknown>;
    externalTours: Array<unknown>;
    floorplans: Array<FloorPlanProps>;
    units: Array<{ name: string }>;
  }> = {},
) => {
  if (tourType === SINGLE_FAMILY) {
    return 'Tour';
  }

  if (tourType === AMENITY) {
    const tourCount = vrModels.length + externalTours.length;
    return `Tour ${tourCount + 1}`;
  }

  let prefix: string | undefined = '';
  const floorPlan = floorplans[floorPlanIndex as number];
  // Unit tour without floor plan
  if (floorPlanIndex === null && unitIndex !== null) {
    prefix = units[unitIndex]?.name;
    // Unit tour with floor plan
  } else if (floorPlanIndex !== null && unitIndex !== null) {
    const unit = floorPlan?.units?.[unitIndex];
    prefix = unit?.name;
  } else {
    prefix = floorPlan?.name;
  }
  return `${prefix || 'New'} tour`;
};

const getModelId = ({
  floorPlanIndex = null,
  unitIndex = null,
  currentProperty: { floorplans, units },
}: {
  floorPlanIndex?: number | null;
  unitIndex?: number | null;
  currentProperty: { floorplans: Array<FloorPlanProps>; units: Array<UnitProps> };
}): string | null => {
  const floorPlan = floorPlanIndex !== null && floorplans[floorPlanIndex];
  // Unit tour without floor plan
  if (floorPlanIndex === null && unitIndex !== null) {
    return (units as Array<UnitProps>)[unitIndex].unitId;
  }

  // Unit tour with floor plan
  if (floorPlanIndex !== null && unitIndex !== null) {
    const unit = floorPlan && floorPlan?.units?.[unitIndex];
    return unit ? unit.unitId : null;
  }
  return floorPlan ? (floorPlan.floorPlanId as string) : null;
};

type GetFormDataFromExternalTourUpdateProps = {
  floorPlanIndex: null | number;
  currentProperty: CurrentPropertyProps;
  tourToUpdate: ExternalTourProps;
  tourType: KeysOfTourType;
  previousVrModelId?: string;
  previousExternalTourUrl?: string;
  unitIndex: null | number;
};
const getFormDataFromExternalTourUpdate = ({
  floorPlanIndex = null,
  currentProperty: { units, floorplans, vrModels, externalTours },
  tourToUpdate = ExternalTour(),
  tourType = AMENITY,
  previousVrModelId = '',
  previousExternalTourUrl = '',
  unitIndex = null,
}: GetFormDataFromExternalTourUpdateProps) => {
  const isUnitTourWithoutFloorPlan = tourType === UNIT && floorPlanIndex === null;
  const isUnitTourWithFloorPlan = tourType === UNIT && floorPlanIndex !== null;
  const isFloorPlanTour = tourType === FLOOR_PLAN;
  const isAmenityTour = tourType === AMENITY;
  const isSingleFamilyTour = tourType === SINGLE_FAMILY;
  if (isUnitTourWithoutFloorPlan) {
    const newUnits: Array<UnitProps> = cloneDeep(units);
    newUnits[unitIndex as number].externalTour = tourToUpdate;
    // delete newUnits[unitIndex].vrModel; // original js to ts below
    newUnits[unitIndex as number].vrModel = VrModel();
    return { units: newUnits };
  }
  if (isUnitTourWithFloorPlan) {
    const newFloorplans: Array<FloorPlanProps> = cloneDeep(floorplans);
    const targetUnit = (newFloorplans[floorPlanIndex] as TargetFloorPlanProps).units[unitIndex as number];
    targetUnit.externalTour = tourToUpdate;
    // delete newFloorplans[floorPlanIndex].units[unitIndex].vrModel; // original js to ts below
    targetUnit.vrModel = VrModel();
    return { floorplans: newFloorplans };
  }
  if (isFloorPlanTour) {
    const newFloorplans = cloneDeep(floorplans);
    newFloorplans[floorPlanIndex as number].externalTour = tourToUpdate;
    // delete newFloorplans[floorPlanIndex].vrModel; // original js to ts below
    newFloorplans[floorPlanIndex as number].vrModel = VrModel();
    return { floorplans: newFloorplans };
  }
  if (isSingleFamilyTour || isAmenityTour) {
    const newExternalTours = cloneDeep(externalTours);
    const newVrModels = cloneDeep(vrModels);
    const currentTourIndex = newExternalTours.findIndex(
      (externalTour: ExternalTourProps) => externalTour.tourUrl === previousExternalTourUrl,
    );
    // Deleted a tour
    if (!tourToUpdate.tourUrl && currentTourIndex > -1) {
      newExternalTours.splice(currentTourIndex, 1);
    } else if (tourToUpdate.tourUrl !== '') {
      // Added a tour
      if (currentTourIndex < 0) {
        newExternalTours.splice(newExternalTours.length, 0, tourToUpdate as never);
        const previousVrModelIndex = newVrModels.findIndex(
          (vrModel: VrModelProps) => vrModel.vrModelId === previousVrModelId,
        );
        // Updated a tour of different type
        if (previousVrModelIndex > -1) {
          newVrModels.splice(previousVrModelIndex, 1);
        }
        // Updated a tour of the same type
      } else {
        newExternalTours.splice(currentTourIndex, 1, tourToUpdate as never);
      }
    }
    return { externalTours: newExternalTours, vrModels: newVrModels };
  }
  return {};
};

type GetFormDataFromZillowTourUpdateProps = {
  floorPlanIndex: null | number;
  currentProperty: CurrentPropertyProps;
  tourToUpdate: VrModelProps;
  tourType: KeysOfTourType;
  previousVrModelId?: string;
  previousExternalTourUrl?: string;
  unitIndex: null | number;
};
const getFormDataFromZillowTourUpdate = ({
  floorPlanIndex = null,
  currentProperty: { units, floorplans, vrModels, externalTours },
  tourToUpdate = VrModel(),
  tourType = AMENITY,
  previousVrModelId = '',
  previousExternalTourUrl = '',
  unitIndex = null,
}: GetFormDataFromZillowTourUpdateProps) => {
  const isUnitTourWithoutFloorPlan = tourType === UNIT && floorPlanIndex === null;
  const isUnitTourWithFloorPlan = tourType === UNIT && floorPlanIndex !== null;
  const isFloorPlanTour = tourType === FLOOR_PLAN;
  const isAmenityTour = tourType === AMENITY;
  const isSingleFamilyTour = tourType === SINGLE_FAMILY;
  if (isUnitTourWithoutFloorPlan) {
    const newUnits: Array<UnitProps> = cloneDeep(units);
    newUnits[unitIndex as number].vrModel = VrModel(tourToUpdate);
    // delete newUnits[unitIndex].externalTour; // original js to ts below
    newUnits[unitIndex as number].externalTour = ExternalTour();
    return { units: newUnits };
  }
  if (isUnitTourWithFloorPlan) {
    const newFloorplans = cloneDeep(floorplans);
    const targetUnit = (newFloorplans[floorPlanIndex] as TargetFloorPlanProps).units[unitIndex as number];
    targetUnit.vrModel = VrModel(tourToUpdate);
    // delete newFloorplans[floorPlanIndex].units[unitIndex].externalTour; // original js to ts below
    targetUnit.externalTour = ExternalTour();
    return { floorplans: newFloorplans };
  }
  if (isFloorPlanTour) {
    const newFloorplans = cloneDeep(floorplans);
    newFloorplans[floorPlanIndex as number].vrModel = tourToUpdate;
    // delete newFloorplans[floorPlanIndex].externalTour; // original js to ts below
    newFloorplans[floorPlanIndex as number].externalTour = ExternalTour();
    return { floorplans: newFloorplans };
  }
  if (isSingleFamilyTour || isAmenityTour) {
    const newExternalTours = cloneDeep(externalTours);
    const newVrModels = cloneDeep(vrModels);
    const currentTourIndex = newVrModels.findIndex((vrModel: VrModelProps) => vrModel.vrModelId === previousVrModelId);

    // Deleted a tour
    if (!tourToUpdate.vrModelId && currentTourIndex > -1) {
      newVrModels.splice(currentTourIndex, 1);
    } else if (tourToUpdate.vrModelId) {
      // Added a tour
      if (currentTourIndex < 0) {
        newVrModels.splice(newVrModels.length, 0, tourToUpdate as never);
        const previousExternalTourIndex = newExternalTours.findIndex(
          (externalTour: ExternalTourProps) => externalTour.tourUrl === previousExternalTourUrl,
        );
        // Updated a tour of different type
        if (previousExternalTourIndex > -1) {
          newExternalTours.splice(previousExternalTourIndex, 1);
        }
        // Updated a tour of the same type
      } else {
        newVrModels.splice(currentTourIndex, 1, tourToUpdate as never);
      }
    }
    return { externalTours: newExternalTours, vrModels: newVrModels };
  }
  return {};
};

type UpdateTourProps = (GetFormDataFromExternalTourUpdateProps | GetFormDataFromZillowTourUpdateProps) & {
  isExternalTour: boolean;
  updateCurrentProperty: (
    arg: ReturnType<typeof getFormDataFromExternalTourUpdate> | ReturnType<typeof getFormDataFromZillowTourUpdate>,
  ) => void;
};
const updateTour = (params: UpdateTourProps) => {
  const newFormData = params.isExternalTour
    ? getFormDataFromExternalTourUpdate(params as GetFormDataFromExternalTourUpdateProps)
    : getFormDataFromZillowTourUpdate(params as GetFormDataFromZillowTourUpdateProps);
  params.updateCurrentProperty(newFormData);
};

export {
  getDefaultTourLabel,
  getModelId,
  getFormDataFromExternalTourUpdate,
  getFormDataFromZillowTourUpdate,
  updateTour,
};
